import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** Selectable color schemes. 'auto' follows day/night (sunrise/sunset for Serbia)
 *  between 'daylight' (day) and 'plum' (night); the rest are fixed schemes.
 *  'classic' is opt-in (see OPTIONAL_APP_THEMES). */
export type AppTheme = 'plum' | 'daylight' | 'blue' | 'contrast' | 'warm' | 'classic' | 'auto';

/**
 * App-specific DEFAULT color scheme. A consuming app may provide it
 * (e.g. `{ provide: DEFAULT_APP_THEME, useValue: 'daylight' }`) so it boots in its
 * own default scheme while SHARING this single core ThemeService. Falls back to
 * 'plum' (the waiter default) when not provided — so existing apps are unchanged.
 * The app's index.html pre-boot script MUST use the same default value to avoid a
 * first-paint flash (keep them in sync).
 */
export const DEFAULT_APP_THEME = new InjectionToken<AppTheme>('DEFAULT_APP_THEME');

/**
 * Opt-in schemes the app adds to its picker. Some schemes are meant for one kind of
 * device only — e.g. 'classic' (light cards on a black scene, the look of the kitchen
 * and customer order boards before themes) — so they stay hidden, and set() falls back
 * to the default, unless the app lists them:
 * `{ provide: OPTIONAL_APP_THEMES, useValue: ['classic'] }`.
 * The app's index.html pre-boot script must know their polarity too.
 */
export const OPTIONAL_APP_THEMES = new InjectionToken<AppTheme[]>('OPTIONAL_APP_THEMES');

export interface ThemeOption {
  key: AppTheme;
  /** Human label shown in the picker. */
  name: string;
  /** Light vs dark polarity — drives the `light`/`dark` class kept for Material
   *  theming, the logo flip and any other `.dark`/`.light` rules. For 'auto' this
   *  is unused (the real polarity is computed from the time of day). */
  polarity: 'dark' | 'light';
  /** Listed only when the app provides it in OPTIONAL_APP_THEMES. */
  optIn?: boolean;
}

/** All schemes, in picker order ('auto' first as the smart option). */
const ALL_THEMES: ThemeOption[] = [
  { key: 'auto', name: 'Automatska (dan/noć)', polarity: 'light' },
  { key: 'plum', name: 'eMeni tamna', polarity: 'dark' },
  { key: 'daylight', name: 'eMeni svetla', polarity: 'light' },
  { key: 'classic', name: 'eMeni klasična', polarity: 'light', optIn: true },
  { key: 'blue', name: 'Plavi fokus', polarity: 'dark' },
  { key: 'contrast', name: 'Visoki kontrast', polarity: 'dark' },
  { key: 'warm', name: 'Topla ugostiteljska', polarity: 'dark' },
];

/** Where the theme used to be persisted, before it moved into the app's device
 *  settings — read only once, by takeLegacyTheme() (the app's settings migration). */
const LEGACY_STORAGE_KEY = 'appTheme';
/** Last resolved polarity for 'auto' — lets index.html's pre-boot script pick the
 *  right scheme before first paint without re-running the sun calculation. */
const AUTO_POLARITY_KEY = 'appThemeAutoPolarity';
const SCHEME_CLASSES = [
  'theme-plum',
  'theme-daylight',
  'theme-blue',
  'theme-contrast',
  'theme-warm',
  'theme-classic',
];

/** Belgrade — used for the 'auto' day/night calculation so NO location permission
 *  is needed (the app is used in Serbia). */
const AUTO_LAT = 44.8125;
const AUTO_LNG = 20.4612;
/** 'auto' resolves to this light scheme by day, this dark scheme by night. */
const AUTO_DAY_SCHEME: AppTheme = 'daylight';
const AUTO_NIGHT_SCHEME: AppTheme = 'plum';

/**
 * Sunrise/sunset (as absolute `Date`s) for a lat/lng on the given date — a compact,
 * dependency-free implementation of the standard sunrise equation (the suncalc
 * algorithm). Timezone-independent: it returns absolute instants, so comparing with
 * `new Date()` works regardless of the device's configured timezone.
 */
function sunTimes(date: Date, lat: number, lng: number): { sunrise: Date; sunset: Date } {
  const rad = Math.PI / 180;
  const dayMs = 86400000, J1970 = 2440588, J2000 = 2451545, J0 = 0.0009;
  const e = rad * 23.4397; // obliquity of the Earth
  const days = date.valueOf() / dayMs - 0.5 + J1970 - J2000;
  const fromJulian = (j: number) => new Date((j + 0.5 - J1970) * dayMs);

  const lw = rad * -lng;
  const phi = rad * lat;
  const n = Math.round(days - J0 - lw / (2 * Math.PI));
  const ds = J0 + lw / (2 * Math.PI) + n; // approx. solar transit (mean)
  const M = rad * (357.5291 + 0.98560028 * ds); // solar mean anomaly
  const L =
    M +
    rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M)) +
    rad * 102.9372 +
    Math.PI; // ecliptic longitude
  const dec = Math.asin(Math.sin(e) * Math.sin(L)); // sun declination
  const Jnoon = J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
  const w = Math.acos(
    (Math.sin(-0.833 * rad) - Math.sin(phi) * Math.sin(dec)) /
      (Math.cos(phi) * Math.cos(dec))
  ); // hour angle for sunrise/sunset (incl. refraction)
  const Jset =
    J2000 + (J0 + (w + lw) / (2 * Math.PI) + n) + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
  return { sunrise: fromJulian(Jnoon - (Jset - Jnoon)), sunset: fromJulian(Jset) };
}

/**
 * App-wide color scheme. Applies a `theme-<key>` class on <html> (drives the
 * `--app-*` tokens in theme.scss) PLUS a `dark`/`light` polarity class (kept for
 * Material theming, the logo flip and the pre-boot inline script in index.html).
 * Does NOT persist the choice: the theme is part of the app's device settings
 * (defaults → profile → local overrides) — the app calls set() from them, and its
 * pre-boot inline script reads them to apply the theme before first paint (no flash).
 *
 * 'auto' is special: it has no scheme class of its own — it resolves to 'daylight'
 * by day and 'plum' by night (sunrise/sunset for Serbia) and re-applies live at the
 * next boundary. The default scheme is configurable per app via DEFAULT_APP_THEME.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** Themes selectable in this app, in picker order ('auto' first as the smart
   *  option); opt-in schemes only when the app lists them in OPTIONAL_APP_THEMES. */
  readonly themes: ThemeOption[];

  readonly theme$ = new BehaviorSubject<AppTheme>('plum');
  private autoTimer?: ReturnType<typeof setTimeout>;

  constructor(
    @Optional()
    @Inject(DEFAULT_APP_THEME)
    private readonly defaultScheme: AppTheme | null = null,
    @Optional()
    @Inject(OPTIONAL_APP_THEMES)
    optionalThemes: AppTheme[] | null = null
  ) {
    this.themes = ALL_THEMES.filter((t) => !t.optIn || !!optionalThemes?.includes(t.key));
    // Default until the app applies the theme from its settings (same task at boot).
    this.set(this.defaultTheme);
  }

  /** The currently chosen theme key (returns 'auto' while in auto mode). */
  get current(): AppTheme {
    return this.theme$.value;
  }

  set(theme: AppTheme): void {
    this.stopAuto();
    if (theme === 'auto') {
      this.applyAuto();
      this.theme$.next('auto');
      return;
    }
    const opt = this.concreteOpt(theme);
    this.applySchemeClasses(opt);
    this.theme$.next(opt.key);
  }

  /** The app's configured default scheme (or 'plum' when none provided). */
  get defaultTheme(): AppTheme {
    return this.defaultScheme &&
      this.themes.some((t) => t.key === this.defaultScheme)
      ? this.defaultScheme
      : 'plum';
  }

  /**
   * One-time migration for the app's device settings: the theme saved under the old
   * `appTheme` key (null if none or unrecognised). The key is removed either way — the
   * theme now lives only in the settings.
   */
  takeLegacyTheme(): AppTheme | null {
    try {
      const v = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (v === null) return null;
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      // migrate the old binary values
      if (v === 'light') return 'daylight';
      if (v === 'dark') return 'plum';
      if (this.themes.some((t) => t.key === v)) {
        return v as AppTheme;
      }
    } catch {
      /* localStorage unavailable — nothing to migrate */
    }
    return null;
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /** Resolve a concrete (non-'auto') scheme option, falling back to the app
   *  default then the first real scheme. */
  private concreteOpt(theme: AppTheme): ThemeOption {
    return (
      this.themes.find((t) => t.key === theme && t.key !== 'auto') ??
      this.themes.find((t) => t.key === this.defaultScheme && t.key !== 'auto') ??
      this.themes.find((t) => t.key !== 'auto')!
    );
  }

  private applySchemeClasses(opt: ThemeOption): void {
    const el = document.documentElement;
    SCHEME_CLASSES.forEach((c) => el.classList.remove(c));
    el.classList.add('theme-' + opt.key);
    el.classList.toggle('light', opt.polarity === 'light');
    el.classList.toggle('dark', opt.polarity === 'dark');
    this.syncThemeColorMeta(el);
  }

  /** Keep the browser/OS chrome colour (`<meta name="theme-color">`) in sync with the
   *  active scheme by reading its resolved header colour. Without this the PWA/mobile
   *  chrome shows a single static colour (from index.html) that matches only one scheme. */
  private syncThemeColorMeta(el: HTMLElement): void {
    try {
      const color = getComputedStyle(el).getPropertyValue('--app-header-1').trim();
      if (!color) return;
      let meta = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', color);
    } catch {
      /* non-browser / SSR — ignore */
    }
  }

  /** 'auto': apply daylight (day) or plum (night) for Serbia, remember the resolved
   *  polarity for the pre-boot script, and schedule the next boundary re-check. */
  private applyAuto(): void {
    const isDay = this.isDaytime(new Date());
    this.applySchemeClasses(this.concreteOpt(isDay ? AUTO_DAY_SCHEME : AUTO_NIGHT_SCHEME));
    try {
      localStorage.setItem(AUTO_POLARITY_KEY, isDay ? 'light' : 'dark');
    } catch {
      /* ignore */
    }
    this.scheduleAutoRecheck();
  }

  private isDaytime(now: Date): boolean {
    const { sunrise, sunset } = sunTimes(now, AUTO_LAT, AUTO_LNG);
    if (isNaN(sunrise.getTime()) || isNaN(sunset.getTime())) return true; // safety
    return now >= sunrise && now < sunset;
  }

  /** Re-apply 'auto' at the next sunrise/sunset boundary (clamped to 30s..6h) so the
   *  theme flips live without a reload. */
  private scheduleAutoRecheck(): void {
    const now = new Date();
    const { sunrise, sunset } = sunTimes(now, AUTO_LAT, AUTO_LNG);
    const upcoming = [sunrise.getTime(), sunset.getTime()].filter((t) => t > now.getTime());
    const next = upcoming.length ? Math.min(...upcoming) : now.getTime() + 60 * 60 * 1000;
    const delay = Math.min(Math.max(next - now.getTime() + 1000, 30 * 1000), 6 * 60 * 60 * 1000);
    this.autoTimer = setTimeout(() => {
      if (this.current === 'auto') this.applyAuto();
    }, delay);
  }

  private stopAuto(): void {
    if (this.autoTimer) {
      clearTimeout(this.autoTimer);
      this.autoTimer = undefined;
    }
  }
}
